import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useMutation, useQuery } from 'react-query';
import { useFormik } from 'formik';
import useNotification from '../../../../../../utils/hooks/useNotification';
import productInventorySchema from '../../../../../../validations/products/productInventorySchema';
import useForm from '../../../../../../utils/hooks/useForm';

// Icons
import { ReactComponent as AccountingIcon } from '../../../../../../assets/icons/accounting.svg';

// Services
import useProductInventoryService from '../../../../../../services/productInventoryService';

// Components
import PageWrapper from '../../../../../layout/PageWrapper/PageWrapper';
import PageSection from '../../../../../layout/PageSection/PageSection';
import SkeletonLoader from '../../../../../molecules/SkeletonLoader/SkeletonLoader';
import Accounting from '../Accounting/Accounting';
import Button from '../../../../../atoms/Button/Button';

function ProductLogistic({ productType }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  const [searchParams] = useSearchParams();
  const productId = useMemo(() => searchParams.get('id'), [searchParams]);

  const [product, setProduct] = useState(null);
  const [initialValues, setInitialValues] = useState({
    accountingCode: '',
    lowSeasonPrice: '',
    mediumSeasonPrice: '',
    highSeasonPrice: '',
    taxRate: '',
  });

  const { getProductInventory, putProductInventory } = useProductInventoryService();

  const getFormattedProductInventory = (values) => ({
    accountingCode: values.accountingCode || '',
    lowSeasonPrice: values.lowSeasonPrice || '',
    mediumSeasonPrice: values.mediumSeasonPrice || '',
    highSeasonPrice: values.highSeasonPrice || '',
    taxRate: values.taxRate || '',
  });

  const { isFetching } = useQuery('getProductInventory', () => getProductInventory({ productId }), {
    enabled: !!productId,
    onSuccess: (res) => {
      if (res?.data) {
        setProduct(res.data);
        setInitialValues(getFormattedProductInventory(res.data));
      }
    },
    onError: () => {
      navigate(`/products/${productType}`);
    },
  });

  const { isLoading: isPutInventoryLoading, mutate: putInventoryMutate } = useMutation(
    (values) => putProductInventory({ productId, productInventory: values }),
    {
      onSuccess: (res) => {
        displayNotification({
          message: t('common.successfullySaved'),
        });

        if (res?.data) {
          setInitialValues(getFormattedProductInventory(res.data));
        }
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'common.saveError'),
          color: 'error',
        });
      },
    },
  );

  useEffect(() => {
    if (!productId) navigate(`/products/${productType}`);
  }, []);

  const formatNum = (price) => (price ? parseFloat(price, 10).toFixed(2).toString() : '0.00');

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        putInventoryMutate({
          accountingCode: values.accountingCode,
          lowSeasonPrice: formatNum(values.lowSeasonPrice),
          mediumSeasonPrice: formatNum(values.mediumSeasonPrice),
          highSeasonPrice: formatNum(values.highSeasonPrice),
          taxRate: formatNum(values.taxRate),
        });
      }
    },
    validationSchema: productInventorySchema,
  });

  return (
    <PageWrapper
      title={product?.name}
      breadcrumb={product?.name ? [
        { label: t(`${productType}.title`), href: `/products/${productType}` },
      ] : null}
    >
      {isFetching ? (
        <>
          <SkeletonLoader height={25} className="m-b-20" />
          <SkeletonLoader height={70} className="m-b-20" />
          <SkeletonLoader height={300} />
        </>
      ) : (
        <PageSection title={t('product.logistic.pricesAccounting')} icon={<AccountingIcon />}>
          <Accounting
            formik={{
              values: formik.values,
              handleChange: formik.handleChange,
              handleBlur: formik.handleBlur,
              setFieldValue: formik.setFieldValue,
              setFieldTouched: formik.setFieldTouched,
              touched: formik.touched,
              errors: formik.errors,
            }}
            productType={productType}
          />

          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPutInventoryLoading}
              disabled={!formik.dirty}
            >
              {t('common.saveModifications')}
            </Button>
          </div>
        </PageSection>
      )}
    </PageWrapper>
  );
}

ProductLogistic.propTypes = {
  productType: PropTypes.string.isRequired,
};

export default ProductLogistic;
